import { useSeoMeta } from '@unhead/react';
import { useArticles } from '@/hooks/useArticles';
import { useAuthor } from '@/hooks/useAuthor';
import { BlogLayout } from '@/components/BlogLayout';
import { ArticleCard } from '@/components/ArticleCard';
import { ProfileAbout } from '@/components/ProfileAbout';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { BLOG_OWNER_PUBKEY, BLOG_TITLE, BLOG_HASHTAG } from '@/lib/blogConfig';

function ArticleListSkeleton() {
  return (
    <div className="grid gap-6">
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i} className="overflow-hidden p-0">
          <Skeleton className="aspect-[16/8] w-full rounded-none" />
          <div className="p-6 space-y-3">
            <Skeleton className="h-3 w-32" />
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
          </div>
        </Card>
      ))}
    </div>
  );
}

function Hero() {
  const author = useAuthor(BLOG_OWNER_PUBKEY);
  const meta = author.data?.metadata;
  const name = meta?.display_name || meta?.name || 'Dikaios1517';

  return (
    <section className="text-center py-12 sm:py-16">
      <Avatar className="mx-auto h-20 w-20 ring-2 ring-primary/30">
        <AvatarImage src={meta?.picture} alt={name} />
        <AvatarFallback className="text-2xl">{name.charAt(0)}</AvatarFallback>
      </Avatar>
      <h1 className="mt-6 font-serif text-4xl sm:text-5xl font-bold tracking-tight text-foreground">
        {BLOG_TITLE}
      </h1>
      <p className="mx-auto mt-4 max-w-xl text-lg leading-relaxed text-muted-foreground">
        {meta?.about ? (
          <ProfileAbout about={meta.about} />
        ) : (
          `Reflections and essays on theology, faith, and Scripture — published on Nostr under #${BLOG_HASHTAG}.`
        )}
      </p>
    </section>
  );
}

const Index = () => {
  const { data: articles, isLoading, error } = useArticles();

  useSeoMeta({
    title: BLOG_TITLE,
    description: `Theology essays and reflections by Dikaios1517, published on Nostr.`,
  });

  return (
    <BlogLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <Hero />

        <div className="pb-8">
          {isLoading && <ArticleListSkeleton />}

          {!isLoading && error && (
            <Card className="border-dashed">
              <CardContent className="py-12 px-8 text-center">
                <p className="text-muted-foreground max-w-sm mx-auto">
                  Couldn't load articles right now. Try checking your relay
                  connections or refresh in a moment.
                </p>
              </CardContent>
            </Card>
          )}

          {!isLoading && !error && articles && articles.length === 0 && (
            <Card className="border-dashed">
              <CardContent className="py-12 px-8 text-center">
                <p className="text-muted-foreground max-w-sm mx-auto">
                  No articles found yet. Articles tagged{' '}
                  <span className="text-primary font-medium">#{BLOG_HASHTAG}</span>{' '}
                  will appear here once they're published.
                </p>
              </CardContent>
            </Card>
          )}

          {!isLoading && articles && articles.length > 0 && (
            <div className="grid gap-6">
              {articles.map((article) => (
                <ArticleCard key={article.event.id} article={article} />
              ))}
            </div>
          )}
        </div>
      </div>
    </BlogLayout>
  );
};

export default Index;
